import { useState } from 'react';

import { adminApi } from '@/api';
import { Badge, Button, Card, CardHeader, EmptyState, Icon } from '@/components/ui';
import { DocumentViewer } from '@/features/verification/DocumentViewer';
import { ADMIN_DOCUMENT_LABELS, DOCUMENT_TONES } from '@/features/verification/status';
import { formatDate, formatFileSize } from '@/utils/format';

import { RejectReasonModal } from './RejectReasonModal';
import { useDocumentDecision } from './useDocumentDecision';

/**
 * A student's CVs on their admin record: open each one, verify it or reject
 * it with a reason -- the same calls as the Verify documents queue, so a
 * decision taken here shows there and the other way round. Every open is
 * audited by the download endpoint.
 */
export function StudentCvsCard({ student }) {
  const [viewing, setViewing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const { approve, reject } = useDocumentDecision({ kind: 'resume', noun: 'CV', student });

  const resumes = student.resumes ?? [];

  return (
    <Card>
      <CardHeader
        title="CVs"
        description="Open a CV to check it against the profile, then verify or reject it."
      />
      {resumes.length === 0 ? (
        <EmptyState compact icon={<Icon name="fileText" size={16} />} title="No CV uploaded" />
      ) : (
        <ul className="divide-line divide-y">
          {resumes.map((resume) => {
            const status = resume.verification_status;
            const busy = approve.isPending && approve.variables?.id === resume.id;
            return (
              <li
                key={resume.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="flex min-w-0 flex-1 basis-64 items-start gap-3">
                  <span className="bg-brand-50 text-brand-600 grid size-10 shrink-0 place-items-center rounded-lg">
                    <Icon name="fileText" size={18} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-ink truncate text-sm font-medium">{resume.file_name}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge dot tone={DOCUMENT_TONES[status] ?? 'neutral'}>
                        {ADMIN_DOCUMENT_LABELS[status] ?? status}
                      </Badge>
                      {resume.is_primary && <Badge tone="brand">Primary</Badge>}
                      <span className="text-ink-muted text-xs">
                        {formatFileSize(resume.size_bytes)} · Uploaded{' '}
                        {formatDate(resume.created_at)}
                      </span>
                    </div>
                    {status === 'REJECTED' && resume.verification_rejection_reason && (
                      <p className="text-danger-600 mt-1 text-xs">
                        Rejected: {resume.verification_rejection_reason}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    title={`Open ${resume.file_name}`}
                    onClick={() => setViewing(resume)}
                  >
                    <Icon name="eye" size={14} />
                    View CV
                  </Button>
                  {status !== 'REJECTED' && (
                    <Button
                      variant="tintDanger"
                      size="sm"
                      title="Reject this CV with a reason"
                      onClick={() => setRejecting(resume)}
                    >
                      <Icon name="close" size={14} />
                      Reject
                    </Button>
                  )}
                  {status !== 'VERIFIED' && (
                    <Button
                      variant="tintPositive"
                      size="sm"
                      title="Mark this CV as verified"
                      isLoading={busy}
                      onClick={() => approve.mutate(resume)}
                    >
                      <Icon name="check" size={14} />
                      Verify
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Shown in the page, not downloaded. Each open is audited. */}
      <DocumentViewer
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title={`${student.full_name}'s CV`}
        fileName={viewing?.file_name}
        contentType={viewing?.content_type}
        queryKey={['resume', viewing?.id]}
        loadUrl={(options) => adminApi.getDocumentDownloadUrl('resume', viewing.id, options)}
      />

      <RejectReasonModal
        open={rejecting !== null}
        title={`Reject ${student.full_name}'s CV`}
        placeholder="The CV lists a degree that is not on the profile"
        isLoading={reject.isPending}
        onClose={() => setRejecting(null)}
        onSubmit={(reason) => reject.mutateAsync({ document: rejecting, reason })}
      />
    </Card>
  );
}
